package app.view

import app.CustomRouter
import app.Routes
import app.api.ProjectApi
import app.api.ProjectReleaseApi
import app.api.RolloutExecutionPaginatedApi
import app.components.companyStageDropdown
import app.components.pagination
import app.components.sidebarNavigation
import app.plugin.Routing
import app.svg.sort
import app.utils.formatDateTime
import app.utils.getDarkStatusBackgroundColor
import dev.fritz2.core.*

fun RenderContext.projectRelease(
    projectApi: ProjectApi,
    projectReleaseApi: ProjectReleaseApi,
    rolloutExecutionPaginatedApi: RolloutExecutionPaginatedApi,
    projectId: String?,
    releaseId: String?,
    page: Int,
    order: String,
) {
    val selectedStage = storeOf("")
    val selectedCompanies = storeOf(emptyList<String>())
    val isDropdownOpen = storeOf(false)

    if (projectId == null || releaseId == null) {
        CustomRouter.navTo(mapOf("route" to Routes.PROJECTS))
        return
    }

    projectApi.handler(projectId)
    projectApi.data.renderNotNull { project ->
        projectReleaseApi.handler(releaseId)
        projectReleaseApi.data.renderNotNull { release ->
            div("flex gap-4 dark:text-white") {
                sidebarNavigation(projectId)
                div("basis-10/12 border rounded-md shadow-md my-4 p-4 dark:bg-gray-800 dark:border-gray-700") {
                    div("flex justify-between mb-4 items-center") {
                        div("flex items-center space-x-2") {
                            a("cursor-pointer hover:underline dark:text-gray-200") {
                                +"Releases"
                                clicks handledBy {
                                    CustomRouter.releases(
                                        mapOf(
                                            "route" to Routes.RELEASES,
                                            Routes.Params.PROJECT_ID to projectId,
                                        )
                                    )
                                }
                            }
                            div {
                                +"/"
                            }
                            div("font-semibold") {
                                +"${release.version}"
                            }
                        }
                        selectedStage.data.render { selectedStageName ->
                            selectedCompanies.data.render { selectedCompanyNames ->
                                button(
                                    "px-2 py-2 rounded text-white " +
                                            (if (selectedCompanyNames.isEmpty())
                                                "bg-gray-400 cursor-not-allowed opacity-60"
                                            else
                                                "bg-blue-500")
                                ) {
                                    +"Deploy"
                                    disabled(selectedCompanyNames.isEmpty())
                                    clicks handledBy {
                                        rolloutExecutionPaginatedApi.newRolloutExecution(
                                            Routing.RolloutExecutions.NewByCompanyName.Body(
                                                releaseId = releaseId.toInt(),
                                                companies = selectedCompanyNames,
                                                stage = selectedStageName

                                            )
                                        )
                                        selectedStage.update("")
                                        selectedCompanies.update(listOf())
                                        isDropdownOpen.update(false)
                                    }
                                }
                            }
                        }
                    }
                    div("flex gap-2 mb-4") {
                        div("font-semibold") {
                            +"Created at:"
                        }
                        div {
                            +formatDateTime(release.createdAt)
                        }
                    }
                    if (release.deployables.isNotEmpty()) {
                        div("mb-4") {
                            div("font-semibold") {
                                +"Deployables:"
                            }
                            release.deployables.forEach { entry ->
                                div {
                                    +"${entry.key}: ${entry.value}"
                                }
                            }
                        }
                    }
                    div {
                        select("w-52 p-2 border rounded mb-4 dark:bg-gray-700 dark:border-gray-600 dark:text-white") {
                            option {
                                value("")
                                +"Select Stage"
                            }
                            project.companyProjects
                                .flatMap { it.stages }
                                .distinctBy { it.name }
                                .sortedBy { it.ordering }
                                .forEach { stage ->
                                    option {
                                        value(stage.name)
                                        +stage.name
                                    }
                                }
                            changes.values() handledBy { value ->
                                selectedCompanies.update(listOf())
                                isDropdownOpen.update(false)
                                selectedStage.update(value)
                            }
                        }
                        selectedStage.data.render { selectedStageName ->
                            selectedCompanies.data.render { selectedCompaniesName ->
                                companyStageDropdown(
                                    selectedStage = selectedStageName,
                                    selectedCompanies = selectedCompaniesName,
                                    companyProjects = project.companyProjects,
                                    isDropdownOpen = isDropdownOpen,
                                    onCompanySelectionChange = { newSelection ->
                                        selectedCompanies.update(newSelection)
                                    }
                                )
                            }
                        }
                    }
                    rolloutExecutionPaginatedApi.handler(
                        RolloutExecutionPaginatedApi.PaginationRequest(
                            id = releaseId,
                            order = order,
                            page = page,
                        )
                    )
                    rolloutExecutionPaginatedApi.data.render { rolloutExecutions ->
                        div("relative overflow-x-auto shadow-md sm:rounded-lg mt-4 border dark:border-gray-700") {
                            table("w-full text-sm text-left text-gray-500 dark:text-gray-400") {
                                thead("text-xs text-gray-700 bg-gray-50 dark:bg-gray-700 dark:text-gray-300") {
                                    tr {
                                        th("px-6 py-3 dark:text-gray-300") {
                                            scope("col")
                                            +"STATUS"
                                        }
                                        th("px-6 py-3") {
                                            scope("col")
                                            button("flex underline") {
                                                +"STARTED AT"
                                                sort("w-3 h-3 ms-1.5")
                                                clicks handledBy {
                                                    CustomRouter.release(
                                                        mapOf(
                                                            "route" to Routes.RELEASE,
                                                            Routes.Params.PROJECT_ID to projectId,
                                                            Routes.Params.RELEASE_ID to releaseId,
                                                            Routes.Params.ORDER to if (order == "asc") "desc" else "asc",
                                                            Routes.Params.PAGE to "1"
                                                        )
                                                    )
                                                }
                                            }
                                        }
                                        th("px-6 py-3") {
                                            scope("col")
                                            +"FINISHED AT"
                                        }
                                        th("px-6 py-3") {
                                            scope("col")
                                            +"TARGET"
                                        }
                                    }
                                }
                                tbody {
                                    rolloutExecutions.items.forEach { rolloutExecution ->
                                        tr(
                                            "bg-white hover:bg-gray-200 cursor-pointer border-b group " +
                                                    "dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 dark:text-gray-300"
                                        ) {
                                            clicks handledBy {
                                                CustomRouter.rollout(
                                                    mapOf(
                                                        "route" to Routes.ROLLOUT,
                                                        Routes.Params.PROJECT_ID to projectId,
                                                        Routes.Params.ROLLOUT_ID to rolloutExecution.id.toString(),
                                                    )
                                                )
                                            }
                                            td("px-6 py-4 flex") {
                                                div(
                                                    "text-xs text-white rounded-full px-1 ${
                                                        getDarkStatusBackgroundColor(
                                                            rolloutExecution
                                                        )
                                                    }"
                                                ) {
                                                    +rolloutExecution.status
                                                    clicks handledBy {
                                                        CustomRouter.release(
                                                            mapOf(
                                                                "route" to Routes.RELEASE,
                                                                Routes.Params.PROJECT_ID to projectId,
                                                                Routes.Params.RELEASE_ID to release.id.toString(),
                                                            )
                                                        )
                                                    }
                                                }
                                            }
                                            td("px-6 py-4") {
                                                +formatDateTime(rolloutExecution.startedAt)
                                            }
                                            td("px-6 py-4") {
                                                +formatDateTime(rolloutExecution.finishedAt)
                                            }
                                            td("px-6 py-4 flex gap-1 text-xs") {
                                                div("bg-blue-500 text-white rounded-full px-1") {
                                                    +rolloutExecution.stageName
                                                }
                                                div("bg-gray-500 text-white rounded-full px-1") {
                                                    +rolloutExecution.companyName
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        div("mt-4 flex justify-center") {
                            pagination(
                                currentPage = rolloutExecutions.currentPage,
                                totalPages = rolloutExecutions.totalPages
                            ) { newPage ->
                                CustomRouter.release(
                                    mapOf(
                                        "route" to Routes.RELEASE,
                                        Routes.Params.PROJECT_ID to projectId,
                                        Routes.Params.RELEASE_ID to releaseId,
                                        Routes.Params.ORDER to order,
                                        Routes.Params.PAGE to newPage.toString()
                                    )
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}
